import type { SupabaseClient } from "@supabase/supabase-js";

export interface RecordWriteContext {
  recordId: string;
  workspaceId: string;
  entityId: string;
  entityTypeId?: string;
  userId: string;
}

export interface RecordWriteContextOptions {
  /** Workspace explicitly selected by the calling surface. */
  preferredWorkspaceId?: string;
  /** Entity lens used to preserve the workspace of existing instances. */
  entityType?: string;
}

/**
 * Resolve write identity from the authenticated user's canonical profile.
 * A new record must never copy tenancy from an arbitrary existing record:
 * that fails on an empty workspace and can cross a tenant boundary when RLS
 * visibility changes.
 */
export async function resolveRecordWriteContext(
  client: SupabaseClient,
  options: RecordWriteContextOptions = {},
): Promise<RecordWriteContext | null> {
  const {
    data: { user },
    error: authError,
  } = await client.auth.getUser();

  if (authError || !user?.id) {
    console.error("resolveRecordWriteContext: authenticated user is required");
    return null;
  }

  // This is the same membership source used by get_tenant_id() in RLS.
  const { data: memberships, error: membershipError } = await client
    .from("vb_workspace_users")
    .select("workspace_id")
    .eq("user_id", user.id)
    .eq("is_active", true);

  // Legacy profiles still carry workspace_id for accounts that predate the
  // canonical membership table. Keep this as a compatibility fallback only.
  const { data: profile, error: profileError } = await client
    .from("user_profiles")
    .select("workspace_id")
    .eq("id", user.id)
    .maybeSingle();

  const activeWorkspaceIds = new Set(
    (memberships ?? [])
      .map((membership) => membership.workspace_id as string | null)
      .filter((workspaceId): workspaceId is string => Boolean(workspaceId)),
  );

  let workspaceId: string | undefined;
  let entityId: string | undefined;

  if (options.preferredWorkspaceId) {
    if (!activeWorkspaceIds.has(options.preferredWorkspaceId)) {
      console.error(
        "resolveRecordWriteContext: preferred workspace is not an active membership",
      );
      return null;
    }
    workspaceId = options.preferredWorkspaceId;
  }

  // An entity lens should keep writing beside its existing instances. This
  // prevents a newly added personal workspace from silently splitting one
  // entity library across tenants. If the entity already spans more than one
  // workspace, only an explicit workspace selection may resolve the ambiguity.
  if (!workspaceId && options.entityType) {
    const { data: entityRows, error: entityRowsError } = await client
      .from("vb_records")
      .select("workspace_id, entity_id, is_library_home:meta->is_library_home")
      .eq("entity_type", options.entityType)
      .eq("is_deleted", false)
      .limit(1000);

    if (entityRowsError) {
      console.error(
        "resolveRecordWriteContext: failed to inspect entity workspaces",
        entityRowsError.message,
      );
      return null;
    }

    const visibleEntityRows = (entityRows ?? []).filter(
      (row) =>
        typeof row.workspace_id === "string" &&
        activeWorkspaceIds.has(row.workspace_id),
    );
    const entityWorkspaceIds = [
      ...new Set(visibleEntityRows.map((row) => row.workspace_id as string)),
    ];

    if (!workspaceId && entityWorkspaceIds.length === 1) {
      workspaceId = entityWorkspaceIds[0];
    } else if (!workspaceId && entityWorkspaceIds.length > 1) {
      console.error(
        "resolveRecordWriteContext: entity spans multiple workspaces; explicit selection is required",
      );
      return null;
    }

    if (workspaceId) {
      // A library's administrative home can live on the documents base while
      // its instances live on the registered entity base (for example vTask).
      // Infer instance identity from actual instances when they exist; keep
      // the existing home-only behavior for an otherwise empty library.
      const instanceRows = visibleEntityRows.filter(
        (row) => row.is_library_home !== true,
      );
      const identityRows = instanceRows.length > 0
        ? instanceRows
        : visibleEntityRows;
      const entityIds = [
        ...new Set(
          identityRows
            .filter((row) => row.workspace_id === workspaceId)
            .map((row) => row.entity_id as string | null)
            .filter((candidate): candidate is string => candidate !== null),
        ),
      ];

      if (entityIds.length === 1) {
        entityId = entityIds[0];
      } else if (entityIds.length > 1) {
        console.error(
          "resolveRecordWriteContext: entity lens has multiple canonical entity bases",
        );
        return null;
      }
    }
  }

  const profileWorkspaceId = profile?.workspace_id as string | undefined;

  // vb_records.entity_id is a required FK to the workspace's canonical entity
  // base. Prefer an exact base (for example `tool`), then the universal
  // `documents` base used by the existing entity lenses. This also gives a new
  // entity its workspace automatically without copying an arbitrary record.
  if (!entityId && activeWorkspaceIds.size > 0) {
    const baseKeys = options.entityType
      ? [...new Set([options.entityType, "documents"])]
      : ["documents"];
    const { data: entityBases, error: entityBasesError } = await client
      .from("vb_entities")
      .select("id, workspace_id, meta_key")
      .in("workspace_id", [...activeWorkspaceIds])
      .in("meta_key", baseKeys);

    if (entityBasesError) {
      console.error(
        "resolveRecordWriteContext: failed to resolve canonical entity base",
        entityBasesError.message,
      );
      return null;
    }

    const workspaceBases = (entityBases ?? []).filter(
      (base) => !workspaceId || base.workspace_id === workspaceId,
    );
    const exactBases = options.entityType
      ? workspaceBases.filter((base) => base.meta_key === options.entityType)
      : [];
    const candidates =
      exactBases.length > 0
        ? exactBases
        : workspaceBases.filter((base) => base.meta_key === "documents");

    const profileCandidate = candidates.find(
      (base) => base.workspace_id === profileWorkspaceId,
    );
    const selectedBase =
      candidates.length === 1
        ? candidates[0]
        : profileCandidate &&
            candidates.filter(
              (base) => base.workspace_id === profileWorkspaceId,
            ).length === 1
          ? profileCandidate
          : undefined;

    if (selectedBase) {
      workspaceId = selectedBase.workspace_id as string;
      entityId = selectedBase.id as string;
    } else if (candidates.length > 1) {
      console.error(
        "resolveRecordWriteContext: multiple workspace bases are available; explicit selection is required",
      );
      return null;
    }
  }

  if (!workspaceId || !entityId) {
    console.error(
      "resolveRecordWriteContext: user has no writable workspace entity base",
      membershipError?.message ?? profileError?.message ?? "missing entity base",
    );
    return null;
  }

  let entityTypeId: string | undefined;
  if (options.entityType) {
    const { data: entityTypeRow, error: entityTypeError } = await client
      .from("entity_types")
      .select("id")
      .eq("slug", options.entityType)
      .maybeSingle();

    if (entityTypeError) {
      console.error(
        "resolveRecordWriteContext: failed to resolve entity type",
        entityTypeError.message,
      );
      return null;
    }
    entityTypeId = entityTypeRow?.id as string | undefined;
  }

  return {
    recordId: crypto.randomUUID(),
    workspaceId: workspaceId as string,
    entityId,
    entityTypeId,
    userId: user.id,
  };
}
